// Print Modal Component - Modal for selecting print mode (GM or Player)
import { EventManagerMixin } from '../utils/event-manager.js';
import { CONFIG } from '../config.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';

class PrintModal extends EventManagerMixin(HTMLElement) {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        super.connectedCallback?.();
        this.render();
        this.setupEventListeners();
    }

    // disconnectedCallback is handled by EventManagerMixin - cleans up all listeners

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: none;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: ${CONFIG.Z_INDEX_MODAL};
                }

                :host(.open) {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .backdrop {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: ${CONFIG.COLORS.OVERLAY_DARKER};
                }

                .modal {
                    position: relative;
                    background: var(--bg-surface, #1e1e2e);
                    border: 1px solid var(--border-default, #444);
                    border-radius: 12px;
                    padding: 1.5rem;
                    min-width: 320px;
                    max-width: 90vw;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
                }

                .modal-header {
                    margin-bottom: 1.5rem;
                    text-align: center;
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 1.25rem;
                    color: var(--text-primary, #e5e5e5);
                }

                .modal-header p {
                    margin: 0.5rem 0 0;
                    font-size: 0.875rem;
                    color: var(--text-secondary, #888);
                }

                .print-options {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .print-option {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 1.25rem 1rem;
                    background: var(--bg-elevated, #2a2a3a);
                    border: 2px solid var(--border-subtle, #333);
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .print-option:hover {
                    border-color: var(--color-primary, #3b82f6);
                    background: var(--bg-input, #1f1f1f);
                }

                .print-option i {
                    font-size: 2rem;
                    margin-bottom: 0.75rem;
                    color: var(--text-primary, #e5e5e5);
                }

                .print-option .label {
                    font-size: 1rem;
                    font-weight: 600;
                    color: var(--text-primary, #e5e5e5);
                    margin-bottom: 0.25rem;
                }

                .print-option .description {
                    font-size: 0.75rem;
                    color: var(--text-secondary, #888);
                    text-align: center;
                }

                .gm-option i {
                    color: #f59e0b;
                }

                .player-option i {
                    color: #10b981;
                }

                .cancel-btn {
                    width: 100%;
                    padding: 0.75rem;
                    background: transparent;
                    border: 1px solid var(--border-default, #444);
                    border-radius: 6px;
                    color: var(--text-secondary, #888);
                    font-size: 0.875rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .cancel-btn:hover {
                    background: var(--bg-elevated, #2a2a3a);
                    color: var(--text-primary, #e5e5e5);
                }

                .loading {
                    display: none;
                    flex-direction: column;
                    align-items: center;
                    padding: 2rem;
                }

                .loading.active {
                    display: flex;
                }

                .loading i {
                    font-size: 2rem;
                    color: var(--color-primary, #3b82f6);
                    animation: spin 1s linear infinite;
                    margin-bottom: 1rem;
                }

                .loading span {
                    color: var(--text-secondary, #888);
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .options-container.hidden {
                    display: none;
                }
            </style>

            <div class="backdrop" id="backdrop"></div>
            <div class="modal">
                <div class="modal-header">
                    <h2><i class="fas fa-print"></i> Print Map</h2>
                    <p>Select which version to print</p>
                </div>

                <div class="options-container" id="options">
                    <div class="print-options">
                        <button class="print-option gm-option" id="gm-btn">
                            <i class="fas fa-crown"></i>
                            <span class="label">GM Map</span>
                            <span class="description">All hexes visible with full imagery</span>
                        </button>
                        <button class="print-option player-option" id="player-btn">
                            <i class="fas fa-users"></i>
                            <span class="label">Player Map</span>
                            <span class="description">Revealed hexes + empty outlines</span>
                        </button>
                    </div>
                    <button class="cancel-btn" id="cancel-btn">Cancel</button>
                </div>

                <div class="loading" id="loading">
                    <i class="fas fa-spinner"></i>
                    <span>Generating map...</span>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        const backdrop = this.shadowRoot.getElementById('backdrop');
        const cancelBtn = this.shadowRoot.getElementById('cancel-btn');
        const gmBtn = this.shadowRoot.getElementById('gm-btn');
        const playerBtn = this.shadowRoot.getElementById('player-btn');

        // Use EventManagerMixin's addManagedListener for automatic cleanup
        this.addManagedListener(backdrop, 'click', () => this.close());
        this.addManagedListener(cancelBtn, 'click', () => this.close());
        this.addManagedListener(gmBtn, 'click', () => this.selectMode('gm'));
        this.addManagedListener(playerBtn, 'click', () => this.selectMode('player'));
    }

    open() {
        this.classList.add('open');
        this.showOptions();
    }

    close() {
        this.classList.remove('open');
        this.showOptions();
    }

    showOptions() {
        const options = this.shadowRoot.getElementById('options');
        const loading = this.shadowRoot.getElementById('loading');
        options.classList.remove('hidden');
        loading.classList.remove('active');
    }

    showLoading() {
        const options = this.shadowRoot.getElementById('options');
        const loading = this.shadowRoot.getElementById('loading');
        options.classList.add('hidden');
        loading.classList.add('active');
    }

    selectMode(mode) {
        this.showLoading();

        // Dispatch event for main.js to handle
        this.dispatchEvent(createHexEvent(HexEvents.PRINT_MODE_SELECTED, { mode }));
    }
}

customElements.define('print-modal', PrintModal);
